import {
  ArtistItemInfoFragment,
  ArtistProjectMetadataFragment,
  GetAllArtistEditorialPagesQuery
} from '@/gql/generated/graphql'
import { artistRoute } from './routes'

export type ArtistItemInfo = {
  slug: string
  url: string
  avatarUrl: string
  walletAddress: string
  name: string
  hasEditorialPage: boolean
  featuredTokenUrl: string
  subtext?: string
  projects: ArtistProjectMetadataFragment[]
}

export const combineArtists = (
  allArtists: ArtistItemInfoFragment[],
  artistPages: GetAllArtistEditorialPagesQuery | undefined
): ArtistItemInfo[] => {
  // Create lookup dictionary for editorial pages
  const artistPagesAddresses = (artistPages?.artistEditorialPages?.data ?? []).reduce(
    (acc, page) => {
      if (page.attributes?.userProfileAddress) {
        const address = page.attributes.userProfileAddress.toLowerCase()
        if (!acc[address]) {
          acc[address] = []
        }
        acc[address].push(page.attributes)
      }
      return acc
    },
    {} as Record<string, any[]>
  )

  // First, group artists by name
  const artistsByName = allArtists.reduce(
    (acc, artist) => {
      const name = artist.user?.display_name ?? ''
      if (!name) return acc // Skip artists without names

      if (!acc[name]) {
        acc[name] = []
      }
      acc[name].push(artist)
      return acc
    },
    {} as Record<string, ArtistItemInfoFragment[]>
  )

  // Then combine the grouped artists
  const combinedArtists = Object.entries(artistsByName)
    .map(([name, artists]) => {
      // Get all unique wallet addresses
      const walletAddresses = [
        ...new Set(artists.map((a) => a.public_address?.toLowerCase()).filter((addr): addr is string => !!addr))
      ]

      // Find all artists with editorial pages
      const artistsWithEditorial = artists.filter((a) => {
        const address = a.public_address?.toLowerCase() ?? ''
        return artistPagesAddresses[address]?.length > 0
      })

      // If no artists have editorial pages, create a single combined artist
      if (artistsWithEditorial.length === 0) {
        return {
          slug: walletAddresses[0],
          url: artistRoute(walletAddresses[0]),
          hasEditorialPage: false,
          avatarUrl: artists[0]?.user?.profile_by_id?.profile_picture?.url ?? '',
          walletAddress: walletAddresses[0],
          name,
          featuredTokenUrl: artists[0]?.projects?.[0]?.featured_token?.[0]?.media_url ?? '',
          projects: artists.reduce(
            (acc, artist) => [...acc, ...(artist.projects ?? [])],
            [] as ArtistProjectMetadataFragment[]
          )
        }
      }

      // Create a separate artist object for each artist with an editorial page
      return artistsWithEditorial.map((artist) => {
        const address = artist.public_address?.toLowerCase() ?? ''
        const editorialPage = artistPagesAddresses[address]?.[0] // Use first page for this artist

        return {
          slug: editorialPage?.slug ?? address,
          url: artistRoute(editorialPage?.slug ?? address),
          hasEditorialPage: true,
          avatarUrl:
            editorialPage?.avatar?.data?.attributes?.url ?? artist.user?.profile_by_id?.profile_picture?.url ?? '',
          walletAddress: address,
          name: editorialPage?.artistName ?? name,
          featuredTokenUrl: artist.projects?.[0]?.featured_token?.[0]?.media_url ?? '',
          projects: editorialPage?.ReleasesSection?.projectMetadata.map((p: any) => p?.Project) ?? artist.projects ?? []
        }
      })
    })
    .flat() // Flatten the array since we might have multiple artists per name
    .filter(
      (artist): artist is ArtistItemInfo => artist !== null && artist.name !== '' && artist.walletAddress.length > 0
    )

  // Add artists that have editorial pages but aren't in allArtists
  const additionalArtists = (artistPages?.artistEditorialPages?.data ?? [])
    .filter((page) => {
      const address = page.attributes?.userProfileAddress?.toLowerCase()
      // Only include if this address isn't already in combinedArtists
      return address && !combinedArtists.some((artist) => artist.walletAddress.toLowerCase() === address)
    })
    .map((page) => {
      const address = page.attributes?.userProfileAddress?.toLowerCase() ?? ''
      return {
        slug: page.attributes?.slug ?? address,
        url: artistRoute(page.attributes?.slug ?? address),
        hasEditorialPage: true,
        avatarUrl: page.attributes?.avatar?.data?.attributes?.url ?? '',
        walletAddress: address,
        name: page.attributes?.artistName ?? '',
        featuredTokenUrl: '',
        projects: page.attributes?.ReleasesSection?.projectMetadata?.map((p: any) => p?.Project) ?? []
      }
    })
    .filter(
      (artist): artist is ArtistItemInfo => artist !== null && artist.name !== '' && artist.walletAddress.length > 0
    )

  return [...combinedArtists, ...additionalArtists]
}

export const isWalletAddress = (text: string): boolean => {
  return !!text.match(/(0x[a-fA-F0-9]{40})/g)
}
export const isEnsAddress = (text: string): boolean => {
  return !!text.match(/([a-zA-Z0-9.-]+\.eth)/g)
}

export const shortenWalletAddress = (address: string): string => {
  return `${address.slice(0, 6)}...${address.slice(-4)}`
}
